// src/index.tsx
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import axios from 'axios';
import App from './App';
import 'bootstrap/dist/css/bootstrap.min.css'; // Import Bootstrap CSS
import 'bootstrap/dist/js/bootstrap.min.js'; // Import Bootstrap CSS

import './styles.scss'; // Your custom styles
import './styles/main.scss';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// ── React-Snap guard ─────────────────────────────────────────────────────────
// During pre-rendering (react-snap / Puppeteer) there is no backend.
// Rejecting requests (even with a plain object) causes unhandled promise
// rejections which Puppeteer surfaces as `pageerror` events — triggering a
// race condition in highland that crashes the entire pre-render pipeline.
// Instead, swap out the axios adapter so every request silently resolves
// with an empty 200 response. Components already handle null/empty data
// via their loading states, so the pre-rendered HTML is a valid skeleton.
if (navigator.userAgent.includes('ReactSnap')) {
  // During pre-rendering there is no backend. We swap out the axios adapter
  // so every request silently resolves instead of throwing an AxiosError
  // (which Puppeteer surfaces as a `pageerror`, crashing highland's stream).
  //
  // The stub data is a recursive Proxy so any property access returns a safe
  // empty value — whether a component expects an array, an object with
  // `.data` / `.total_pages`, or calls `.filter()` / `.map()` etc.
  const EMPTY: never[] = [];
  const ARRAY_METHODS = new Set(Object.getOwnPropertyNames(Array.prototype));

  const makeStub = (): any => new Proxy(EMPTY as any, {
    get(_target, prop: string | symbol) {
      if (prop === 'length')           return 0;
      if (prop === 'then')             return undefined;           // not a thenable
      if (prop === Symbol.iterator)    return EMPTY[Symbol.iterator].bind(EMPTY);
      if (prop === Symbol.toPrimitive) return () => 0;
      if (prop === Symbol.toStringTag) return 'Array';
      if (prop === 'toJSON')           return () => [];
      // Numeric index → undefined (no items)
      if (typeof prop === 'string' && !Number.isNaN(Number(prop))) return undefined;
      // Real array methods (filter, map, slice, find, forEach, …) →
      // bind to an empty real array so they work correctly and return []
      if (typeof prop === 'string' && ARRAY_METHODS.has(prop)) {
        return (Array.prototype as any)[prop].bind(EMPTY);
      }
      // Any other named property (.data, .total, .blogs, .items, …) → new stub
      return makeStub();
    },
  });

  axios.defaults.adapter = (config) =>
    Promise.resolve({
      data: makeStub(),
      status: 200,
      statusText: 'OK (ReactSnap stub)',
      headers: {},
      config,
      request: {},
    } as any);
}
// ─────────────────────────────────────────────────────────────────────────────

const rootElement = document.getElementById('root') as HTMLElement;

const app = (
  <React.StrictMode>
    <AuthProvider>
      <CartProvider>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </CartProvider>
    </AuthProvider>
  </React.StrictMode>
);
if (rootElement.hasChildNodes()) {
  // Pre-rendered by react-snap – hydrate to preserve static HTML
  hydrateRoot(rootElement, app);
} else {
  // Normal CSR fallback
  createRoot(rootElement).render(app);
}